import React from "react";
import { ClipboardList, Clock3, Copy, Download, LoaderCircle, RefreshCcw, SlidersHorizontal } from "lucide-react";
import useAccess from "../hooks/useAccess.jsx";
import useDiagnostics from "../hooks/useDiagnostics.js";
import { canAccessLocalToolsPage, canManageSystemDiagnostics } from "../lib/accessControl.js";
import { buildSystemDiagnosticsAnalysis } from "../lib/systemDiagnosticsAnalysis.js";
import {
  fetchSystemConfigState,
  fetchSystemDiagnosticsSnapshots,
  fetchSystemPerformanceReport,
  fetchRailwaySystemPerformanceAvailability,
  fetchRailwaySystemPerformanceReport,
  toggleSystemDiagnostics,
  updateRailwaySystemPerformanceCollection,
  updateSystemPerformanceCollection,
} from "../lib/systemConfigApi.js";
import { confirmInGrain } from "../utils/grainDialogs.js";
import {
  buildPerformanceReport,
  buildRailwayPerformanceReport,
  downloadPerformanceReport,
  serializePerformanceReport,
} from "../utils/performanceReport.js";
import { joinClasses } from "./resultsPrimitives/shared.js";

const SYSTEM_DIAGNOSTICS_ANALYSIS_SNAPSHOT_LIMIT = 500;
const ACTION_CLASS_NAME = "inline-flex min-h-8 min-w-0 items-center justify-center gap-2 rounded-[4px] border px-3 py-1 text-sm font-semibold focus-visible:ring-2 focus-visible:ring-accent/30 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";
const PRIMARY_ACTION_CLASS_NAME = `${ACTION_CLASS_NAME} border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong`;
const SECONDARY_ACTION_CLASS_NAME = `${ACTION_CLASS_NAME} border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900`;
const pillToneClasses = {
  outline: "border border-slate-300 bg-white text-slate-700",
  info: "border border-accent bg-white text-accent-strong",
  success: "border border-accent bg-accent text-white",
  warning: "border border-amber-700 bg-white text-amber-800",
  danger: "border border-red-600 bg-white text-red-700",
};
const FEEDBACK_TONE_CLASS_NAMES = {
  danger: "border border-red-600 bg-white text-red-700",
  info: "border border-slate-300 bg-white text-slate-700",
  success: "border border-accent bg-white text-accent-strong",
  warning: "border border-amber-700 bg-white text-amber-800",
};

function resolveFeedbackToneClassName(tone) {
  return FEEDBACK_TONE_CLASS_NAMES[tone] || FEEDBACK_TONE_CLASS_NAMES.info;
}

const DIAGNOSTICS_ANALYSIS_TREND_PILL_CLASS_NAMES = {
  insufficient: pillToneClasses.outline,
  moderate: pillToneClasses.warning,
  stable: pillToneClasses.success,
  strong: pillToneClasses.danger,
};

const DIAGNOSTICS_ANALYSIS_SUSPICION_PILL_CLASS_NAMES = {
  backend: pillToneClasses.danger,
  disk: pillToneClasses.info,
  frontend: pillToneClasses.warning,
  insufficient: pillToneClasses.outline,
  mixed: pillToneClasses.warning,
};

function readPerformanceReportClockMs() {
  try {
    if (typeof globalThis?.performance?.now === "function") {
      const value = Number(globalThis.performance.now());
      if (Number.isFinite(value)) {
        return value;
      }
    }
  } catch {
    // Date.now reste une horloge sûre lorsque Performance API est indisponible.
  }

  return Date.now();
}

function resolvePerformanceCollectionPayload(payload) {
  const candidate = payload?.performance_collection && typeof payload.performance_collection === "object"
    ? payload.performance_collection
    : payload;
  return candidate && typeof candidate === "object" && !Array.isArray(candidate)
    ? candidate
    : null;
}

function formatHistoryDate(value) {
  if (!value) {
    return "Date indisponible";
  }

  const parsedDate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "Date indisponible";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(parsedDate);
}

function resolveDiagnosticsAnalysisTrendPillClassName(trendKey) {
  return DIAGNOSTICS_ANALYSIS_TREND_PILL_CLASS_NAMES[trendKey] || pillToneClasses.outline;
}

function resolveDiagnosticsSuspicionPillClassName(suspicionKey) {
  return DIAGNOSTICS_ANALYSIS_SUSPICION_PILL_CLASS_NAMES[suspicionKey] || pillToneClasses.outline;
}

export default function SystemDiagnosticsSection({
  railwayOnly = false,
  sectionIdPrefix = "system-diagnostics-section",
  performanceCollectionState = null,
}) {
  const resolveDiagnosticsSectionId = (sectionKey) => `${sectionIdPrefix}-${sectionKey}`;
  const { access } = useAccess();
  const diagnostics = useDiagnostics();
  const [state, setState] = React.useState(() => performanceCollectionState
    ? { performance_collection: performanceCollectionState }
    : null);
  const [isLoading, setIsLoading] = React.useState(!performanceCollectionState);
  const [loadError, setLoadError] = React.useState("");
  const [feedback, setFeedback] = React.useState(null);
  const [diagnosticsAnalysis, setDiagnosticsAnalysis] = React.useState(null);
  const [diagnosticsAnalysisError, setDiagnosticsAnalysisError] = React.useState("");
  const [diagnosticsCopyFeedback, setDiagnosticsCopyFeedback] = React.useState(null);
  const [isPreparingDiagnosticsAnalysis, setIsPreparingDiagnosticsAnalysis] = React.useState(false);
  const [isDiagnosticsUpdating, setIsDiagnosticsUpdating] = React.useState(false);
  const [performanceReport, setPerformanceReport] = React.useState(null);
  const [performanceReportError, setPerformanceReportError] = React.useState("");
  const [performanceReportFeedback, setPerformanceReportFeedback] = React.useState(null);
  const [performanceCollectionFeedback, setPerformanceCollectionFeedback] = React.useState(null);
  const [isGeneratingPerformanceReport, setIsGeneratingPerformanceReport] = React.useState(false);
  const [isPerformanceCollectionUpdating, setIsPerformanceCollectionUpdating] = React.useState(false);
  const [railwayPerformanceAvailability, setRailwayPerformanceAvailability] = React.useState(null);
  const [railwayPerformanceReport, setRailwayPerformanceReport] = React.useState(null);
  const [railwayPerformanceError, setRailwayPerformanceError] = React.useState("");
  const [railwayPerformanceFeedback, setRailwayPerformanceFeedback] = React.useState(null);
  const [isGeneratingRailwayPerformanceReport, setIsGeneratingRailwayPerformanceReport] = React.useState(false);
  const [isRailwayPerformanceCollectionUpdating, setIsRailwayPerformanceCollectionUpdating] = React.useState(false);

  const canManageDiagnostics = canManageSystemDiagnostics(access);
  const canViewDiagnostics = !railwayOnly && canManageDiagnostics;
  const canToggleDiagnostics = canViewDiagnostics;
  const canUseRailwayDiagnostics = railwayOnly && canManageDiagnostics && canAccessLocalToolsPage(access);
  const performanceCollection = resolvePerformanceCollectionPayload(state?.performance_collection);
  const isPerformanceCollectionActive = performanceCollection?.is_active !== false;
  const railwayPerformanceCollection = resolvePerformanceCollectionPayload(
    railwayPerformanceAvailability?.performance_collection,
  );
  const isRailwayPerformanceCollectionActive = railwayPerformanceCollection?.is_active !== false;
  const isRailwayPerformanceAvailable = railwayPerformanceAvailability?.available === true;

  React.useEffect(() => {
    if (!canUseRailwayDiagnostics) {
      setRailwayPerformanceAvailability(null);
      return undefined;
    }

    let isCancelled = false;
    fetchRailwaySystemPerformanceAvailability()
      .then((payload) => {
        if (!isCancelled) {
          setRailwayPerformanceAvailability(payload);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setRailwayPerformanceAvailability({
            available: false,
            message: error instanceof Error
              ? error.message
              : "Impossible de vérifier le diagnostic Railway de secours.",
            reason: "availability_request_failed",
            target: "railway_production",
          });
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [canUseRailwayDiagnostics]);

  const loadState = React.useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      setState(await fetchSystemConfigState());
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Impossible de charger l’état de la collecte du serveur.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!canViewDiagnostics) return;
    if (performanceCollectionState) {
      setState({ performance_collection: performanceCollectionState });
      setLoadError("");
      setIsLoading(false);
    } else {
      void loadState();
    }
  }, [canViewDiagnostics, loadState, performanceCollectionState]);

  const serializedPerformanceReport = React.useMemo(
    () => performanceReport == null ? "" : serializePerformanceReport(performanceReport),
    [performanceReport],
  );
  const serializedRailwayPerformanceReport = React.useMemo(
    () => railwayPerformanceReport == null ? "" : serializePerformanceReport(railwayPerformanceReport),
    [railwayPerformanceReport],
  );

  const handleGeneratePerformanceReport = React.useCallback(async () => {
    if (!canViewDiagnostics || isGeneratingPerformanceReport) {
      return;
    }

    setIsGeneratingPerformanceReport(true);
    setPerformanceReport(null);
    setPerformanceReportError("");
    setPerformanceReportFeedback(null);

    try {
      const fetchStartedAt = readPerformanceReportClockMs();
      const serverReport = await fetchSystemPerformanceReport();
      const reportFetchDurationMs = Math.max(0, readPerformanceReportClockMs() - fetchStartedAt);
      setPerformanceReport(buildPerformanceReport(serverReport, { reportFetchDurationMs }));
    } catch (error) {
      setPerformanceReportError(
        error instanceof Error ? error.message : "Impossible de générer le rapport de performance.",
      );
    } finally {
      setIsGeneratingPerformanceReport(false);
    }
  }, [canViewDiagnostics, isGeneratingPerformanceReport]);

  const handleTogglePerformanceCollection = React.useCallback(async () => {
    if (!canToggleDiagnostics || !state || isLoading || isPerformanceCollectionUpdating) {
      return;
    }

    const nextIsActive = !isPerformanceCollectionActive;
    if (nextIsActive) {
      const isConfirmed = await confirmInGrain(
          "Démarrer une nouvelle collecte effacera les mesures actuellement figées. Voulez-vous continuer?",
        );
      if (!isConfirmed) {
        return;
      }
    }

    setIsPerformanceCollectionUpdating(true);
    setPerformanceCollectionFeedback(null);
    try {
      const payload = await updateSystemPerformanceCollection({ is_active: nextIsActive });
      const nextPerformanceCollection = resolvePerformanceCollectionPayload(payload);
      if (!nextPerformanceCollection || typeof nextPerformanceCollection.is_active !== "boolean") {
        throw new Error("La réponse de la collecte de performance est incomplète.");
      }

      setState((currentState) => ({
        ...(currentState || {}),
        performance_collection: nextPerformanceCollection,
      }));
      if (nextPerformanceCollection.is_active) {
        setPerformanceReport(null);
        setPerformanceReportError("");
        setPerformanceReportFeedback(null);
      }
      setPerformanceCollectionFeedback({
        tone: nextPerformanceCollection.is_active ? "success" : "info",
        message: nextPerformanceCollection.is_active
          ? "Une nouvelle collecte de performance a démarré; les anciennes mesures ont été effacées."
          : "La collecte est arrêtée. Les mesures déjà recueillies restent disponibles dans le rapport.",
      });
    } catch (error) {
      setPerformanceCollectionFeedback({
        tone: "danger",
        message: error instanceof Error
          ? error.message
          : "Impossible de mettre à jour la collecte de performance.",
      });
    } finally {
      setIsPerformanceCollectionUpdating(false);
    }
  }, [
    canToggleDiagnostics,
    state,
    isLoading,
    isPerformanceCollectionActive,
    isPerformanceCollectionUpdating,
  ]);

  const handleCopyPerformanceReport = React.useCallback(async () => {
    if (!serializedPerformanceReport) {
      return;
    }

    setPerformanceReportFeedback(null);
    try {
      const clipboard = globalThis?.navigator?.clipboard;
      if (!clipboard || typeof clipboard.writeText !== "function") {
        throw new Error("Presse-papiers indisponible dans ce navigateur.");
      }

      await clipboard.writeText(serializedPerformanceReport);
      setPerformanceReportFeedback({
        tone: "success",
        message: "Rapport JSON copié dans le presse-papiers.",
      });
    } catch (error) {
      setPerformanceReportFeedback({
        tone: "danger",
        message: error instanceof Error ? error.message : "Impossible de copier le rapport JSON.",
      });
    }
  }, [serializedPerformanceReport]);

  const handleDownloadPerformanceReport = React.useCallback(() => {
    if (!performanceReport) {
      return;
    }

    setPerformanceReportFeedback(null);
    try {
      const fileName = downloadPerformanceReport(performanceReport);
      setPerformanceReportFeedback({
        tone: "success",
        message: `Rapport téléchargé : ${fileName}`,
      });
    } catch (error) {
      setPerformanceReportFeedback({
        tone: "danger",
        message: error instanceof Error ? error.message : "Impossible de télécharger le rapport JSON.",
      });
    }
  }, [performanceReport]);

  const handleGenerateRailwayPerformanceReport = React.useCallback(async () => {
    if (!isRailwayPerformanceAvailable || isGeneratingRailwayPerformanceReport) {
      return;
    }

    setIsGeneratingRailwayPerformanceReport(true);
    setRailwayPerformanceReport(null);
    setRailwayPerformanceError("");
    setRailwayPerformanceFeedback(null);
    try {
      const fetchStartedAt = readPerformanceReportClockMs();
      const payload = await fetchRailwaySystemPerformanceReport();
      const reportFetchDurationMs = Math.max(0, readPerformanceReportClockMs() - fetchStartedAt);
      setRailwayPerformanceReport(buildRailwayPerformanceReport(payload, { reportFetchDurationMs }));
      const nextCollection = resolvePerformanceCollectionPayload(payload?.performance_collection);
      if (nextCollection) {
        setRailwayPerformanceAvailability((current) => ({
          ...(current || {}),
          performance_collection: nextCollection,
        }));
      }
      setRailwayPerformanceFeedback({
        tone: payload?.warning ? "warning" : "success",
        message: payload?.warning || "Rapport du processus Railway récupéré.",
      });
    } catch (error) {
      setRailwayPerformanceError(
        error instanceof Error ? error.message : "Impossible de générer le rapport Railway.",
      );
    } finally {
      setIsGeneratingRailwayPerformanceReport(false);
    }
  }, [isGeneratingRailwayPerformanceReport, isRailwayPerformanceAvailable]);

  const handleToggleRailwayPerformanceCollection = React.useCallback(async () => {
    if (!isRailwayPerformanceAvailable || isRailwayPerformanceCollectionUpdating) {
      return;
    }

    const nextIsActive = !isRailwayPerformanceCollectionActive;
    if (nextIsActive) {
      const isConfirmed = await confirmInGrain(
          "Démarrer une nouvelle collecte Railway effacera les mesures actuellement figées en production. Voulez-vous continuer?",
        );
      if (!isConfirmed) {
        return;
      }
    }

    setIsRailwayPerformanceCollectionUpdating(true);
    setRailwayPerformanceError("");
    setRailwayPerformanceFeedback(null);
    try {
      const fetchStartedAt = readPerformanceReportClockMs();
      const payload = await updateRailwaySystemPerformanceCollection({ is_active: nextIsActive });
      const reportFetchDurationMs = Math.max(0, readPerformanceReportClockMs() - fetchStartedAt);
      const nextCollection = resolvePerformanceCollectionPayload(payload?.performance_collection);
      if (!nextCollection || typeof nextCollection.is_active !== "boolean") {
        throw new Error("La réponse de la collecte Railway est incomplète.");
      }
      setRailwayPerformanceAvailability((current) => ({
        ...(current || {}),
        performance_collection: nextCollection,
      }));
      if (nextCollection.is_active) {
        setRailwayPerformanceReport(null);
      } else if (payload?.report) {
        setRailwayPerformanceReport(buildRailwayPerformanceReport(payload, { reportFetchDurationMs }));
      }
      setRailwayPerformanceFeedback({
        tone: payload?.warning ? "warning" : nextCollection.is_active ? "success" : "info",
        message: payload?.warning || (nextCollection.is_active
          ? "Une nouvelle collecte de performance a démarré sur Railway."
          : "La collecte Railway est arrêtée et son rapport figé a été récupéré."),
      });
    } catch (error) {
      setRailwayPerformanceFeedback({
        tone: "danger",
        message: error instanceof Error
          ? error.message
          : "Impossible de mettre à jour la collecte Railway.",
      });
    } finally {
      setIsRailwayPerformanceCollectionUpdating(false);
    }
  }, [
    isRailwayPerformanceAvailable,
    isRailwayPerformanceCollectionActive,
    isRailwayPerformanceCollectionUpdating,
  ]);

  const handleCopyRailwayPerformanceReport = React.useCallback(async () => {
    if (!serializedRailwayPerformanceReport) {
      return;
    }
    setRailwayPerformanceFeedback(null);
    try {
      const clipboard = globalThis?.navigator?.clipboard;
      if (!clipboard || typeof clipboard.writeText !== "function") {
        throw new Error("Presse-papiers indisponible dans ce navigateur.");
      }
      await clipboard.writeText(serializedRailwayPerformanceReport);
      setRailwayPerformanceFeedback({ tone: "success", message: "Rapport Railway copié." });
    } catch (error) {
      setRailwayPerformanceFeedback({
        tone: "danger",
        message: error instanceof Error ? error.message : "Impossible de copier le rapport Railway.",
      });
    }
  }, [serializedRailwayPerformanceReport]);

  const handleDownloadRailwayPerformanceReport = React.useCallback(() => {
    if (!railwayPerformanceReport) {
      return;
    }
    setRailwayPerformanceFeedback(null);
    try {
      const fileName = downloadPerformanceReport(railwayPerformanceReport);
      setRailwayPerformanceFeedback({ tone: "success", message: `Rapport téléchargé : ${fileName}` });
    } catch (error) {
      setRailwayPerformanceFeedback({
        tone: "danger",
        message: error instanceof Error ? error.message : "Impossible de télécharger le rapport Railway.",
      });
    }
  }, [railwayPerformanceReport]);

  const handleToggleDiagnostics = React.useCallback(async () => {
    if (!canToggleDiagnostics || isDiagnosticsUpdating) {
      return;
    }

    const nextIsActive = !diagnostics.isActive;
    setIsDiagnosticsUpdating(true);
    setFeedback(null);

    try {
      await toggleSystemDiagnostics({ is_active: nextIsActive });
      diagnostics.setActive(nextIsActive);
      setFeedback({
        tone: nextIsActive ? "success" : "info",
        message: nextIsActive
          ? "Diagnostic performance activé. Un snapshot léger est pris immédiatement puis toutes les 30 minutes, avec persistance côté serveur tant que cet onglet reste leader."
          : "Diagnostic performance désactivé. La collecte automatique est arrêtée pour ce navigateur.",
      });
    } catch (error) {
      setFeedback({
        tone: "danger",
        message: error instanceof Error ? error.message : "Impossible de mettre à jour le diagnostic performance.",
      });
    } finally {
      setIsDiagnosticsUpdating(false);
    }
  }, [canToggleDiagnostics, diagnostics, isDiagnosticsUpdating]);

  const handlePrepareDiagnosticsAnalysis = React.useCallback(async () => {
    if (!canToggleDiagnostics || isPreparingDiagnosticsAnalysis) {
      return;
    }

    setIsPreparingDiagnosticsAnalysis(true);
    setDiagnosticsAnalysis(null);
    setDiagnosticsAnalysisError("");
    setDiagnosticsCopyFeedback(null);

    try {
      const snapshotsPayload = await fetchSystemDiagnosticsSnapshots({
        limit: SYSTEM_DIAGNOSTICS_ANALYSIS_SNAPSHOT_LIMIT,
      });
      setDiagnosticsAnalysis(buildSystemDiagnosticsAnalysis(snapshotsPayload));
    } catch (error) {
      setDiagnosticsAnalysisError(error instanceof Error ? error.message : "Impossible de préparer l'analyse diagnostics.");
    } finally {
      setIsPreparingDiagnosticsAnalysis(false);
    }
  }, [canToggleDiagnostics, isPreparingDiagnosticsAnalysis]);

  const handleCopyDiagnosticsPrompt = React.useCallback(async () => {
    const promptToCopy = diagnosticsAnalysis?.copilotPrompt || "";
    if (!promptToCopy) {
      return;
    }

    setDiagnosticsCopyFeedback(null);

    try {
      const clipboard = globalThis?.navigator?.clipboard;
      if (!clipboard || typeof clipboard.writeText !== "function") {
        throw new Error("Presse-papiers indisponible dans ce navigateur.");
      }

      await clipboard.writeText(promptToCopy);
      setDiagnosticsCopyFeedback({
        tone: "success",
        message: "Prompt Copilot copié dans le presse-papiers.",
      });
    } catch (error) {
      setDiagnosticsCopyFeedback({
        tone: "danger",
        message: error instanceof Error ? error.message : "Impossible de copier le prompt Copilot.",
      });
    }
  }, [diagnosticsAnalysis]);

  if (!canViewDiagnostics && !canUseRailwayDiagnostics) return null;

  return (
    <section aria-labelledby={resolveDiagnosticsSectionId("diagnostic-title")} className="grid min-w-0 scroll-mt-44 gap-3" id={resolveDiagnosticsSectionId("diagnostic")}>
      <h2 className="m-0 text-base font-semibold text-slate-950" id={resolveDiagnosticsSectionId("diagnostic-title")}>{railwayOnly ? "Pilotage Railway à distance" : "Diagnostic et performance"}</h2>
      {canViewDiagnostics && isLoading ? <p role="status" className="m-0 text-sm text-slate-600">Chargement de l’état de la collecte du serveur…</p> : null}
      {canViewDiagnostics && loadError ? <div role="alert" className="grid gap-2 rounded-[4px] border border-red-600 bg-white p-3 text-sm text-red-700">
        <p className="m-0">{loadError}</p>
        <button type="button" className={`${SECONDARY_ACTION_CLASS_NAME} w-fit`} onClick={loadState}>Réessayer</button>
      </div> : null}
      {canViewDiagnostics && feedback ? <p role="status" className={joinClasses(["m-0 rounded-[4px] px-3 py-2 text-sm", resolveFeedbackToneClassName(feedback.tone)])}>{feedback.message}</p> : null}
    {canViewDiagnostics ? (
      <article
        className={joinClasses([
          "grid gap-3 rounded-[4px] border border-slate-300 bg-white px-3.5 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]",
          "scroll-mt-24",
        ])}
        data-system-performance-report="true"
        id={resolveDiagnosticsSectionId("performance")}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid min-w-0 max-w-3xl gap-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <p className="m-0 text-[0.94rem] font-semibold tracking-[-0.02em] text-slate-950">
                Rapport de performance
              </p>
              <span
                className={joinClasses([
                  "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.12em]",
                  !performanceCollection ? pillToneClasses.outline : isPerformanceCollectionActive ? pillToneClasses.success : pillToneClasses.warning,
                ])}
                data-system-performance-collection-status="true"
              >
                {!performanceCollection ? isLoading ? "Vérification…" : "État indisponible" : isPerformanceCollectionActive ? "Collecte active" : "Collecte arrêtée"}
              </span>
            </div>
            <p className="m-0 text-[0.8rem] leading-5 text-slate-600">
              Affiche les mesures déjà recueillies en mémoire par le serveur auquel vous êtes connecté et y ajoute les métriques disponibles dans ce navigateur. Cette action ne démarre aucune collecte.
            </p>
          </div>

          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <button
              className={SECONDARY_ACTION_CLASS_NAME}
              data-system-performance-collection-toggle="true"
              disabled={!state || isLoading || isPerformanceCollectionUpdating}
              onClick={handleTogglePerformanceCollection}
              type="button"
            >
              {isPerformanceCollectionUpdating ? (
                <LoaderCircle className="animate-spin" size={15} aria-hidden="true" />
              ) : isPerformanceCollectionActive ? (
                <Clock3 size={15} aria-hidden="true" />
              ) : (
                <RefreshCcw size={15} aria-hidden="true" />
              )}
              {isPerformanceCollectionActive ? "Arrêter la collecte" : "Démarrer une nouvelle collecte"}
            </button>
            <button
              className={joinClasses([PRIMARY_ACTION_CLASS_NAME, "w-full sm:w-auto"])}
              disabled={isGeneratingPerformanceReport || isPerformanceCollectionUpdating}
              onClick={handleGeneratePerformanceReport}
              type="button"
            >
              {isGeneratingPerformanceReport ? (
                <LoaderCircle className="animate-spin" size={15} aria-hidden="true" />
              ) : (
                <ClipboardList size={15} aria-hidden="true" />
              )}
              Générer le rapport de performance
            </button>
          </div>
        </div>

        {performanceCollectionFeedback ? (
          <div
            aria-live="polite"
            className={joinClasses(["rounded-[4px] px-3 py-2 text-[0.78rem] font-medium", resolveFeedbackToneClassName(performanceCollectionFeedback.tone)])}
          >
            {performanceCollectionFeedback.message}
          </div>
        ) : null}

        {performanceReportError ? (
          <div
            className={joinClasses(["rounded-[4px] px-3 py-2 text-[0.78rem] font-medium", resolveFeedbackToneClassName("danger")])}
            role="alert"
          >
            {performanceReportError}
          </div>
        ) : null}

        {isGeneratingPerformanceReport ? (
          <div className="flex items-center gap-2 rounded-[4px] border border-slate-300 bg-white px-3 py-3 text-[0.78rem] font-medium text-slate-700">
            <LoaderCircle className="animate-spin" size={15} aria-hidden="true" />
            Génération du rapport en cours…
          </div>
        ) : null}

        {performanceReport ? (
          <div className="grid gap-2 rounded-[4px] border border-slate-200 bg-white px-3 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="m-0 text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-slate-600">
                Rapport JSON
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button className={SECONDARY_ACTION_CLASS_NAME} onClick={handleCopyPerformanceReport} type="button">
                  <Copy size={15} aria-hidden="true" />
                  Copier
                </button>
                <button className={SECONDARY_ACTION_CLASS_NAME} onClick={handleDownloadPerformanceReport} type="button">
                  <Download size={15} aria-hidden="true" />
                  Télécharger JSON
                </button>
              </div>
            </div>

            {performanceReportFeedback ? (
              <div
                aria-live="polite"
                className={joinClasses(["rounded-[4px] px-3 py-2 text-[0.76rem] font-medium", resolveFeedbackToneClassName(performanceReportFeedback.tone)])}
              >
                {performanceReportFeedback.message}
              </div>
            ) : null}

            <pre
              className="m-0 min-w-0 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-[4px] bg-slate-950 px-3 py-3 font-mono text-[0.72rem] leading-5 text-slate-100"
              data-system-performance-report-json="true"
              tabIndex={0}
            >
              {serializedPerformanceReport}
            </pre>
          </div>
        ) : null}
      </article>
    ) : null}

    {canUseRailwayDiagnostics && railwayPerformanceAvailability?.reason !== "production_environment" ? (
      <article
        className={joinClasses([
          "grid gap-3 rounded-[4px] border border-slate-300 bg-white px-3.5 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]",
          "scroll-mt-24",
        ])}
        data-railway-system-performance-report="true"
        id={resolveDiagnosticsSectionId("railway-performance")}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid min-w-0 max-w-3xl gap-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <p className="m-0 text-[0.94rem] font-semibold tracking-[-0.02em] text-slate-950">
                Railway — production
              </p>
              <span
                className={joinClasses([
                  "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.12em]",
                  !isRailwayPerformanceAvailable
                    ? pillToneClasses.warning
                    : isRailwayPerformanceCollectionActive
                      ? pillToneClasses.success
                      : pillToneClasses.warning,
                ])}
                data-railway-system-performance-collection-status="true"
              >
                {!isRailwayPerformanceAvailable
                  ? "Indisponible"
                  : isRailwayPerformanceCollectionActive
                    ? "Collecte active"
                    : "Collecte arrêtée"}
              </span>
            </div>
            <p className="m-0 text-[0.8rem] leading-5 text-slate-600">
              Pilote directement le diagnostic mémoire du processus TransIA sur Railway par le canal PostgreSQL de secours. Les métriques du navigateur ajoutées au fichier proviennent de cette console locale.
            </p>
          </div>

          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <button
              className={SECONDARY_ACTION_CLASS_NAME}
              data-railway-system-performance-collection-toggle="true"
              disabled={!isRailwayPerformanceAvailable || isRailwayPerformanceCollectionUpdating || isGeneratingRailwayPerformanceReport}
              onClick={handleToggleRailwayPerformanceCollection}
              type="button"
            >
              {isRailwayPerformanceCollectionUpdating ? (
                <LoaderCircle className="animate-spin" size={15} aria-hidden="true" />
              ) : isRailwayPerformanceCollectionActive ? (
                <Clock3 size={15} aria-hidden="true" />
              ) : (
                <RefreshCcw size={15} aria-hidden="true" />
              )}
              {isRailwayPerformanceCollectionActive
                ? "Arrêter la collecte Railway"
                : "Démarrer une nouvelle collecte Railway"}
            </button>
            <button
              className={joinClasses([PRIMARY_ACTION_CLASS_NAME, "w-full sm:w-auto"])}
              disabled={!isRailwayPerformanceAvailable || isGeneratingRailwayPerformanceReport || isRailwayPerformanceCollectionUpdating}
              onClick={handleGenerateRailwayPerformanceReport}
              type="button"
            >
              {isGeneratingRailwayPerformanceReport ? (
                <LoaderCircle className="animate-spin" size={15} aria-hidden="true" />
              ) : (
                <ClipboardList size={15} aria-hidden="true" />
              )}
              Générer le rapport de performance Railway
            </button>
          </div>
        </div>

        {!isRailwayPerformanceAvailable && railwayPerformanceAvailability?.message ? (
          <div className={joinClasses([
            "rounded-[4px] px-3 py-2 text-[0.78rem] font-medium",
            resolveFeedbackToneClassName("warning"),
          ])}>
            {railwayPerformanceAvailability.message}
          </div>
        ) : null}

        {railwayPerformanceFeedback ? (
          <div
            aria-live="polite"
            className={joinClasses([
              "rounded-[4px] px-3 py-2 text-[0.78rem] font-medium",
              resolveFeedbackToneClassName(railwayPerformanceFeedback.tone),
            ])}
          >
            {railwayPerformanceFeedback.message}
          </div>
        ) : null}

        {railwayPerformanceError ? (
          <div
            className={joinClasses([
              "rounded-[4px] px-3 py-2 text-[0.78rem] font-medium",
              resolveFeedbackToneClassName("danger"),
            ])}
            role="alert"
          >
            {railwayPerformanceError}
          </div>
        ) : null}

        {isGeneratingRailwayPerformanceReport ? (
          <div className="flex items-center gap-2 rounded-[4px] border border-slate-300 bg-white px-3 py-3 text-[0.78rem] font-medium text-slate-700">
            <LoaderCircle className="animate-spin" size={15} aria-hidden="true" />
            Interrogation du processus Railway en cours…
          </div>
        ) : null}

        {railwayPerformanceReport ? (
          <div className="grid gap-2 rounded-[4px] border border-slate-200 bg-white px-3 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="m-0 text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-slate-600">
                Rapport JSON Railway
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button className={SECONDARY_ACTION_CLASS_NAME} onClick={handleCopyRailwayPerformanceReport} type="button">
                  <Copy size={15} aria-hidden="true" />
                  Copier
                </button>
                <button className={SECONDARY_ACTION_CLASS_NAME} onClick={handleDownloadRailwayPerformanceReport} type="button">
                  <Download size={15} aria-hidden="true" />
                  Télécharger JSON
                </button>
              </div>
            </div>
            <pre
              className="m-0 min-w-0 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-[4px] bg-slate-950 px-3 py-3 font-mono text-[0.72rem] leading-5 text-slate-100"
              data-railway-system-performance-report-json="true"
              tabIndex={0}
            >
              {serializedRailwayPerformanceReport}
            </pre>
          </div>
        ) : null}
      </article>
    ) : null}

    {canViewDiagnostics ? (
      <article
        className={joinClasses([
          "grid gap-2.5 rounded-[4px] border border-slate-300 bg-white px-3.5 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]",
          "scroll-mt-24",
        ])}
        id={resolveDiagnosticsSectionId("diagnostics")}
      >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid gap-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <p className="m-0 text-[0.94rem] font-semibold tracking-[-0.02em] text-slate-950">Diagnostic performance</p>
            <span className={joinClasses([
              "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.12em]",
              diagnostics.isActive ? pillToneClasses.success : pillToneClasses.danger,
            ])}>
              {diagnostics.isActive ? "Actif" : "Inactif"}
            </span>
          </div>
          <p className="m-0 text-[0.8rem] leading-5 text-slate-600">
            Active le scheduler diagnostic de ce navigateur: un snapshot applicatif léger est pris immédiatement puis toutes les 30 minutes et persisté côté serveur pour analyse ultérieure.
          </p>
        </div>

        {canToggleDiagnostics ? (
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <button
              aria-pressed={diagnostics.isActive}
              className={joinClasses([
                diagnostics.isActive ? PRIMARY_ACTION_CLASS_NAME : SECONDARY_ACTION_CLASS_NAME,
                "w-full sm:w-auto",
              ])}
              disabled={isDiagnosticsUpdating}
              onClick={handleToggleDiagnostics}
              type="button"
            >
              {isDiagnosticsUpdating ? <LoaderCircle className="animate-spin" size={15} aria-hidden="true" /> : <SlidersHorizontal size={15} aria-hidden="true" />}
              🔍 Diagnostic Performance
            </button>

            <button
              className={joinClasses([
                SECONDARY_ACTION_CLASS_NAME,
                "w-full sm:w-auto",
              ])}
              disabled={isPreparingDiagnosticsAnalysis}
              onClick={handlePrepareDiagnosticsAnalysis}
              type="button"
            >
              {isPreparingDiagnosticsAnalysis ? <LoaderCircle className="animate-spin" size={15} aria-hidden="true" /> : <ClipboardList size={15} aria-hidden="true" />}
              Préparer analyse Copilot
            </button>
          </div>
        ) : (
          <span className={joinClasses([
            "inline-flex items-center rounded-[4px] px-2.5 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.12em]",
            pillToneClasses.outline,
          ])}>
            Lecture seule
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 text-[0.76rem] text-slate-600">
        <span className="inline-flex items-center rounded-[4px] border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700">
          Réinitialisations: {diagnostics.resetCount}
        </span>
        <span className="inline-flex items-center rounded-[4px] border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700">
          Événements: {diagnostics.eventCount}
        </span>
        <span className="inline-flex items-center rounded-[4px] border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700">
          Snapshots session: {diagnostics.snapshotCount}
        </span>
        <span className="inline-flex items-center rounded-[4px] border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700">
          Dernier snapshot: {diagnostics.lastSnapshotAt ? formatHistoryDate(diagnostics.lastSnapshotAt) : "Aucun"}
        </span>
        <span className="inline-flex items-center rounded-[4px] border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700">
          Prochain snapshot: {diagnostics.isActive && diagnostics.nextSnapshotAt ? formatHistoryDate(diagnostics.nextSnapshotAt) : "Arrêté"}
        </span>
        <span className="inline-flex items-center rounded-[4px] border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700">
          Depuis: {diagnostics.activatedAt ? formatHistoryDate(diagnostics.activatedAt) : "Jamais activé"}
        </span>
      </div>

      {diagnostics.lastSnapshotStatus === "error" && diagnostics.lastSnapshotError ? (
        <p className="m-0 text-[0.76rem] font-medium leading-5 text-danger">
          Dernier envoi snapshot: {diagnostics.lastSnapshotError}
        </p>
      ) : null}

      <div className="grid gap-2 rounded-[4px] border border-slate-200 bg-white px-3 py-3 shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="grid gap-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <p className="m-0 text-[0.84rem] font-semibold tracking-[-0.02em] text-slate-950">Synthèse Copilot</p>
              {diagnosticsAnalysis?.status === "ready" ? (
                <span className={joinClasses([
                  "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.12em]",
                  resolveDiagnosticsSuspicionPillClassName(diagnosticsAnalysis.suspicion.key),
                ])}>
                  {diagnosticsAnalysis.suspicion.label}
                </span>
              ) : null}
            </div>
            <p className="m-0 text-[0.76rem] leading-5 text-slate-600">
              Lit les snapshots déjà collectés, synthétise les tendances et prépare un texte prêt à copier-coller dans GitHub Copilot, sans envoi automatique.
            </p>
          </div>

          {diagnosticsAnalysis?.status === "ready" ? (
            <button className={SECONDARY_ACTION_CLASS_NAME} onClick={handleCopyDiagnosticsPrompt} type="button">
              <Copy size={15} aria-hidden="true" />
              Copier le prompt
            </button>
          ) : null}
        </div>

        {diagnosticsCopyFeedback ? (
          <div className={joinClasses(["rounded-[4px] px-3 py-2 text-[0.76rem] font-medium", resolveFeedbackToneClassName(diagnosticsCopyFeedback.tone)])}>
            {diagnosticsCopyFeedback.message}
          </div>
        ) : null}

        {diagnosticsAnalysisError ? (
          <div className={joinClasses(["rounded-[4px] px-3 py-2 text-[0.76rem] font-medium", resolveFeedbackToneClassName("danger")])}>
            {diagnosticsAnalysisError}
          </div>
        ) : null}

        {isPreparingDiagnosticsAnalysis ? (
          <div className="flex items-center gap-2 rounded-[4px] border border-slate-200 bg-slate-50 px-3 py-3 text-[0.78rem] font-medium text-slate-700">
            <LoaderCircle className="animate-spin" size={15} aria-hidden="true" />
            Préparation de l'analyse des snapshots diagnostics…
          </div>
        ) : diagnosticsAnalysis == null ? (
          <div className="rounded-[4px] border border-dashed border-slate-200 bg-slate-50/70 px-3 py-3 text-[0.78rem] leading-5 text-slate-600">
            Utilisez le bouton « Préparer analyse Copilot » pour charger les snapshots collectés, produire une synthèse lisible et générer un prompt prêt à copier.
          </div>
        ) : diagnosticsAnalysis.status === "empty" ? (
          <div className={joinClasses(["rounded-[4px] px-3 py-3 text-[0.78rem] leading-5", resolveFeedbackToneClassName("info")])}>
            {diagnosticsAnalysis.executiveSummaryLines.join(" ")}
          </div>
        ) : (
          <>
            <div className="grid gap-1">
              <p className="m-0 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Résumé exécutif</p>
              <ul className="m-0 grid gap-1 pl-4 text-[0.78rem] leading-5 text-slate-700">
                {diagnosticsAnalysis.executiveSummaryLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>

            <div className="grid gap-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="m-0 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Métriques clés</p>
                <span className={joinClasses([
                  "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.12em]",
                  pillToneClasses.outline,
                ])}>
                  {diagnosticsAnalysis.analyzedSnapshotCount} snapshot(s)
                </span>
              </div>

              <div className="grid gap-2 min-[1200px]:grid-cols-2">
                {diagnosticsAnalysis.keyMetrics.map((metric) => (
                  <div key={metric.key} className="grid gap-1 rounded-[4px] border border-slate-200 bg-slate-50/70 px-3 py-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="m-0 text-[0.76rem] font-semibold tracking-[-0.01em] text-slate-950">{metric.label}</p>
                      <span className={joinClasses([
                        "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.12em]",
                        resolveDiagnosticsAnalysisTrendPillClassName(metric.trendKey),
                      ])}>
                        {metric.trendLabel}
                      </span>
                    </div>
                    <p className="m-0 text-[0.76rem] font-medium leading-5 text-slate-700">{metric.detail}</p>
                    {metric.note ? (
                      <p className="m-0 text-[0.72rem] leading-4.5 text-slate-500">{metric.note}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-2 min-[1200px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <div className="grid gap-1 rounded-[4px] border border-slate-200 bg-slate-50/70 px-3 py-2.5">
                <p className="m-0 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Dérives suspectes</p>
                {diagnosticsAnalysis.suspectedDrifts.length ? (
                  <ul className="m-0 grid gap-1 pl-4 text-[0.76rem] leading-5 text-slate-700">
                    {diagnosticsAnalysis.suspectedDrifts.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="m-0 text-[0.76rem] leading-5 text-slate-600">Aucune hausse modérée ou forte détectée sur la fenêtre analysée.</p>
                )}
              </div>

              <div className="grid gap-1 rounded-[4px] border border-slate-200 bg-slate-50/70 px-3 py-2.5">
                <p className="m-0 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Métriques stables</p>
                {diagnosticsAnalysis.stableMetrics.length ? (
                  <ul className="m-0 grid gap-1 pl-4 text-[0.76rem] leading-5 text-slate-700">
                    {diagnosticsAnalysis.stableMetrics.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="m-0 text-[0.76rem] leading-5 text-slate-600">Pas de stabilité nette exploitable sur les métriques comparables.</p>
                )}
              </div>
            </div>

            <div className="grid gap-1">
              <p className="m-0 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Prompt Copilot prêt à copier</p>
              <p className="m-0 text-[0.76rem] leading-5 text-slate-600">Ce texte reste local dans l'interface jusqu'à votre copie manuelle.</p>
              <textarea
                readOnly
                value={diagnosticsAnalysis.copilotPrompt}
                className="min-h-56 w-full resize-y rounded-[4px] border border-slate-200 bg-slate-950 px-3 py-3 font-mono text-[0.72rem] leading-5 text-slate-100 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle"
              />
            </div>
          </>
        )}
      </div>
      </article>
    ) : null}
    </section>
  );
}
